// Utilities
import { cn } from '@/lib/utils';

// UI primitives
import { Button } from '@ui/button';

// Shared components
import { DataTableProvider } from '@components/DataTable/DataTableProvider';
import { DataTableContent } from '@components/DataTable/DataTableContent';
import { EmptyBoxIcon } from '@icons/EmptyBoxIcon';

// Local components
import { useResaleItemColumns } from '../columns';

// Hooks
import { useTranslation } from '@hooks/useTranslation';

// Types
import type { ResaleItemsTableProps } from '../types';

function EmptyResaleItemsState({ title }: { title: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center py-16">
      <EmptyBoxIcon className="mb-4" />
      <p className="font-inter text-sm text-grey">{title}</p>
    </div>
  );
}

function ErrorResaleItemsState({ title, retryLabel, onRetry }: { title: string; retryLabel: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex h-full flex-col items-center justify-center gap-4 py-16"
    >
      <EmptyBoxIcon />
      <p className="font-inter text-sm text-grey">{title}</p>
      {onRetry && (
        <Button variant="tertiary" size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

export function ResaleItemsTable({
  resaleItems,
  currentStatus,
  onStatusToggle,
  onItemClick,
  isLoading = false,
  isFetching = false,
  isError = false,
  onRetry,
  canWrite = true,
  className,
}: ResaleItemsTableProps) {
  const { t } = useTranslation();
  const columns = useResaleItemColumns({ status: currentStatus, onStatusToggle, onItemClick, canWrite });

  const emptyState = isError ? (
    <ErrorResaleItemsState
      title={t('common.table.error')}
      retryLabel={t('common.table.retry')}
      onRetry={onRetry}
    />
  ) : (
    <EmptyResaleItemsState title={t('common.table.empty')} />
  );

  return (
    <div className={cn('flex flex-col', className)}>
      <DataTableProvider
        columns={columns}
        data={resaleItems}
        isLoading={isLoading}
        isFetching={isFetching}
      >
        <DataTableContent emptyState={emptyState} />
      </DataTableProvider>
    </div>
  );
}
